import AWS from 'aws-sdk';
import { v4 as uuidv4 } from 'uuid';

// AWS S3 설정 (AWS SDK v2)
AWS.config.update({
    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
    region: process.env.REACT_APP_AWS_REGION,
});

const s3 = new AWS.S3();

/**
 * S3에 파일을 업로드하는 함수 (AWS SDK v2 사용 - 브라우저 안전)
 * @param {File} file - 업로드할 파일
 * @returns {Promise<string>} - 업로드된 파일의 URL
 */
export const uploadFileToS3 = async (file) => {
    // 파일명을 안전하게 변환 (한글 및 특수문자 제거/변환)
    const safeName = file.name
        .replace(/[^a-zA-Z0-9.-]/g, '_') // 알파벳, 숫자, 점, 하이픈을 제외한 모든 문자를 언더스코어로 변경
        .replace(/_+/g, '_'); // 연속된 언더스코어를 하나로 축약
    
    const fileName = `${uuidv4()}-${safeName}`;
    const params = {
        Bucket: process.env.REACT_APP_S3_BUCKET,
        Key: fileName,
        Body: file,
        ContentType: file.type,
    };
    
    try {
        const result = await s3.upload(params).promise();
        return result.Location;
    } catch (err) {
        console.error('S3 업로드 에러:', err);
        return null;
    }
};

// 기존 함수들도 유지 (호환성을 위해)
export const uploadToS3 = uploadFileToS3;

/**
 * 파일 유효성 검사
 * @param {File} file - 검사할 파일
 * @param {number} maxSize - 최대 파일 크기 (바이트, 기본값: 5MB)
 * @param {array} allowedTypes - 허용되는 파일 타입 배열
 * @returns {boolean} - 유효성 검사 결과
 */
export const validateFile = (file, maxSize = 50 * 1024 * 1024, allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']) => {
    if (!file) {
        throw new Error('파일이 선택되지 않았습니다.');
    }

    if (file.size > maxSize) {
        throw new Error(`파일 크기가 너무 큽니다. 최대 ${maxSize / (1024 * 1024)}MB까지 업로드 가능합니다.`);
    }

    if (!allowedTypes.includes(file.type)) {
        throw new Error(`지원하지 않는 파일 형식입니다. 허용되는 형식: ${allowedTypes.join(', ')}`);
    }

    return true;
};

/**
 * 이미지 파일 리사이즈 함수
 * @param {File} file - 리사이즈할 이미지 파일
 * @param {number} maxWidth - 최대 너비
 * @param {number} maxHeight - 최대 높이
 * @param {number} quality - 압축 품질 (0-1)
 * @returns {Promise<File>} - 리사이즈된 파일
 */
export const resizeImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            // 비율 계산
            let { width, height } = img;
            
            if (width > height) {
                if (width > maxWidth) {
                    height = (height * maxWidth) / width;
                    width = maxWidth;
                }
            } else {
                if (height > maxHeight) {
                    width = (width * maxHeight) / height;
                    height = maxHeight;
                }
            }

            canvas.width = width;
            canvas.height = height;

            // 이미지 그리기
            ctx.drawImage(img, 0, 0, width, height);

            // Blob으로 변환
            canvas.toBlob(
                (blob) => {
                    const resizedFile = new File([blob], file.name, {
                        type: file.type,
                        lastModified: Date.now(),
                    });
                    resolve(resizedFile);
                },
                file.type,
                quality
            );
        };

        img.src = URL.createObjectURL(file);
    });
};

/**
 * 여러 파일을 동시에 S3에 업로드하는 함수
 * @param {FileList|Array} files - 업로드할 파일들
 * @param {string} folder - S3 버킷 내 폴더명
 * @param {Function} onProgress - 진행률 콜백 (선택사항)
 * @returns {Promise<Array<string>>} - 업로드된 파일들의 URL 배열
 */
export const uploadMultipleToS3 = async (files, folder = 'banners', onProgress = null) => {
    const fileArray = Array.from(files);
    const uploadPromises = [];
    const uploadedUrls = [];

    for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        
        // 파일 유효성 검사
        try {
            validateFile(file);
        } catch (error) {
            console.warn(`파일 ${file.name} 스킵: ${error.message}`);
            continue;
        }

        const uploadPromise = uploadToS3(file, folder)
            .then(url => {
                uploadedUrls.push(url);
                if (onProgress) {
                    onProgress(uploadedUrls.length, fileArray.length);
                }
                return url;
            })
            .catch(error => {
                console.error(`파일 ${file.name} 업로드 실패:`, error);
                return null;
            });

        uploadPromises.push(uploadPromise);
    }

    await Promise.all(uploadPromises);
    return uploadedUrls.filter(url => url !== null);
};

/**
 * S3에서 파일을 삭제하는 함수 (AWS SDK v3는 별도 구현 필요)
 * @param {string} fileUrl - 삭제할 파일의 URL
 * @returns {Promise<boolean>} - 삭제 성공 여부
 */
export const deleteFromS3 = async (fileUrl) => {
    try {
        // URL에서 키 추출
        const url = new URL(fileUrl);
        const key = url.pathname.substring(1); // 첫 번째 '/' 제거

        // 삭제는 별도 AWS SDK 구현이 필요하므로 여기서는 로그만 출력
        console.log('삭제할 파일 키:', key);
        console.warn('파일 삭제 기능은 별도 서버 구현이 필요합니다.');
        return true;
    } catch (error) {
        console.error('S3 삭제 에러:', error);
        throw error;
    }
}; 